import { getDiscountApprovers, getJobSearchWithFilters, type JobFilters } from 'queries';
import { QueryFunctionContext, useQuery } from 'react-query';
import type { JobSearchResult, JsonDataItem, SystemUser } from 'types';
import { Toast, parseErrors } from 'helpers';

const queryGroup = 'JOB_SEARCH';

type SearchByChoices = 'job_id' | 'all' | 'technician_id' | 'customer_id' | 'created_by';
type SearchParams = {
  searchBy: SearchByChoices;
  searchValue: string;
  recordsPerPage?: number;
  page?: number;
  filter?: JobFilters | JobFilters[];
  createdFrom?: string; // YYYY-MM-DD
  createdTo?: string; // YYYY-MM-DD
};

async function queryFn({
  queryKey,
}: QueryFunctionContext<[string, SearchParams]>): Promise<{ jobs: JobSearchResult[]; meta: any }> {
  const [_, params] = queryKey;

  const { searchBy, searchValue, page, recordsPerPage, createdFrom, createdTo, filter } = params;

  const response = await getJobSearchWithFilters({
    page,
    recordsPerPage,
    filter,
    technicianID: searchBy === 'technician_id' ? searchValue : undefined,
    customerID: searchBy === 'customer_id' ? searchValue : undefined,
    userID: searchBy === 'created_by' ? searchValue : undefined,
    jobID: searchBy === 'job_id' ? searchValue : undefined,
    createdFrom,
    createdTo,
  });
  const jobs = response?.data?.map((d) => d.job);
  return { jobs, meta: response?.meta };
}

type JobsWithMetadata = [jobs?: JobSearchResult[], totalJobs?: number, isLoading?: boolean, jobCreators?: SystemUser[]];

function useJobSearch({
  searchBy = 'job_id',
  searchValue = '',
  page = 1,
  recordsPerPage = 5,
  createdFrom,
  createdTo,
  filter,
}: Partial<SearchParams> = {}): JobsWithMetadata {
  const { data, isFetching } = useQuery({
    queryFn,
    queryKey: [queryGroup, { searchBy, searchValue, page, recordsPerPage, createdFrom, createdTo, filter }],
    keepPreviousData: true,
    staleTime: 5000,
    onError(err: JsonDataItem) {
      Toast.show({ description: parseErrors(err).join(', '), type: Toast.Type.Error });
    },
  });

  const { data: jobCreators } = useQuery(['job-creators'], getDiscountApprovers, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  return [data?.jobs, data?.meta.total, isFetching, jobCreators];
}

export { useJobSearch };
